/**
 * 관리자 › 회사 계정·정보공개 동의 (63차, 2026-09-16)
 *   GET  /api/onto/company/admin/list?status=      목록(회사 행 + 마지막 동의)
 *   POST /api/onto/company/admin/review           검토(상태·자사 키)
 *   POST /api/onto/company/admin/consent          대리 동의 기록(근거 필수)
 *   POST /api/onto/company/admin/create           관리자 기반 회사 등록 → 계정은 `회원추가` 탭(업체코드 = 사업자번호 앞 6자리)
 *   GET  /api/onto/company/admin/consented-keys   파이프라인 키 묶음(검증+동의)
 * admin_token(localStorage) Bearer. 색은 파랑·회색만.
 */
import { useCallback, useEffect, useState } from "react";
import { STATUS_LABEL, ROLE_LABEL, formatBusinessNo, fillFromLookup } from "../../audit/disclosureConsent";

const S = {
  wrap: { padding: 20, fontSize: 13, color: "#1f2937", fontFamily: "inherit" },
  h2: { fontSize: 18, fontWeight: 800, margin: "0 0 4px" },
  muted: { color: "#6b7280" },
  row: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "10px 0" },
  btn: { border: "1px solid #d1d5db", background: "#fff", color: "#1f2937", borderRadius: 6, padding: "5px 10px", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" },
  primary: { border: "1px solid #1d4ed8", background: "#1d4ed8", color: "#fff", borderRadius: 6, padding: "5px 10px", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", fontWeight: 700 },
  input: { padding: "5px 8px", fontSize: 12.5, border: "1px solid #d1d5db", borderRadius: 6, fontFamily: "inherit" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 8 },
  th: { textAlign: "left", padding: "7px 8px", background: "#f8fafc", borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap" },
  td: { padding: "7px 8px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" },
  card: { border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginTop: 12, background: "#fff" },
  label: { fontSize: 11.5, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 2 },
};

const token = () => { try { return localStorage.getItem("admin_token"); } catch (_) { return null; } };
const hdr = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token()}` });

async function call(base, path, body) {
  const r = await fetch(`${base}${path}`, body ? { method: "POST", headers: hdr(), body: JSON.stringify(body) } : { headers: hdr() });
  const d = await r.json().catch(() => ({}));
  if (r.status === 401) throw new Error("관리자 토큰이 만료되었습니다. #admin 에서 다시 로그인해 주세요.");
  if (!r.ok) throw new Error(d.message || d.error || `HTTP ${r.status}`);
  return d;
}

export default function AdminCompanyAccounts({ API_BASE_URL = "" }) {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(null);
  const [keys, setKeys] = useState(null);

  const load = useCallback(() => {
    setErr(null);
    call(API_BASE_URL, `/api/onto/company/admin/list${status ? `?status=${status}` : ""}`)
      .then((d) => setRows(d.companies || []))
      .catch((e) => setErr(e.message));
  }, [API_BASE_URL, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={S.wrap}>
      <h2 style={S.h2}>회사 계정 · 정보공개 동의</h2>
      <div style={S.muted}>사업자번호 단위 회사 행과 신고분포 정보공개 동의(신고비율 수준)를 관리합니다. <b>로그인 계정은 회사 줄의 `검토`에서 발급</b>합니다 — 관리자가 초기 비밀번호를 정합니다(구 `회원추가` 탭은 비밀번호를 만들지 않아 로그인되지 않습니다).</div>
      <div style={S.row}>
        <label>상태
          <select style={{ ...S.input, marginLeft: 6 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">전체</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <button style={S.btn} onClick={load}>새로고침</button>
        <button style={S.btn} onClick={() => call(API_BASE_URL, "/api/onto/company/admin/consented-keys").then(setKeys).catch((e) => setErr(e.message))}>파이프라인 키 보기</button>
      </div>
      {err && <div style={{ color: "#b91c1c" }}>{err}</div>}
      {keys && (
        <div style={S.card}>
          <b>검증·동의 회사의 자사 키</b> <span style={S.muted}>({keys.consent_version})</span>
          <div style={{ marginTop: 4 }}>사업자번호 {keys.business_nos?.length || 0} · 사무소 id {(keys.office_ids || []).join(", ") || "—"} · 화주 사업자번호 {(keys.domestic_codes || []).length}</div>
          <div style={S.muted}>loginhs-df 파이프라인이 공개 집계 포함 기준으로 읽는 묶음입니다.</div>
        </div>
      )}
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>사업자번호</th><th style={S.th}>회사명</th><th style={S.th}>역할</th><th style={S.th}>상태</th>
            <th style={S.th}>자사 키</th><th style={S.th}>정보공개 동의</th><th style={S.th}>요청</th><th style={S.th}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td style={{ ...S.td, ...S.muted }} colSpan={8}>회사 행이 없습니다.</td></tr>}
          {rows.map((r) => (
            <tr key={r.businessNo}>
              <td style={{ ...S.td, fontVariantNumeric: "tabular-nums" }}>{formatBusinessNo(r.businessNo)}<div style={S.muted}>그룹 {r.groupId || "—"}</div></td>
              <td style={S.td}>{r.corpName || <span style={S.muted}>회사 행 없음(동의만)</span>}{r.ceoName ? <div style={S.muted}>대표 {r.ceoName}</div> : null}</td>
              <td style={S.td}>{ROLE_LABEL[r.role] || "—"}</td>
              <td style={S.td}>{r.status ? (STATUS_LABEL[r.status] || r.status) : "—"}{r.reviewedAt ? <div style={S.muted}>{r.reviewedAt}</div> : null}</td>
              <td style={S.td}>{r.officeIds ? `사무소 ${r.officeIds}` : ""}{r.officeIds && r.domesticCodes ? " · " : ""}{r.domesticCodes ? `화주 ${r.domesticCodes}` : ""}{!r.officeIds && !r.domesticCodes ? <span style={S.muted}>—</span> : null}</td>
              <td style={S.td}>
                {r.consent ? (
                  <><b style={{ color: r.consent.agreed ? "#1d4ed8" : "#1f2937" }}>{r.consent.agreed ? "동의" : "철회"}</b>
                    <div style={S.muted}>{r.consent.createdAt} · {r.consent.userId || "관리자"} · {r.consent.channel}</div></>
                ) : <span style={S.muted}>기록 없음</span>}
              </td>
              <td style={S.td}>{r.requestedBy || "—"}{r.requestedAt ? <div style={S.muted}>{r.requestedAt}</div> : null}</td>
              <td style={S.td}><button style={S.btn} onClick={() => setSel(r)}>검토</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {sel && <ReviewCard row={sel} base={API_BASE_URL} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); }} />}
      <CreateCard base={API_BASE_URL} onDone={load} />
    </div>
  );
}

/**
 * 70차: 회사 로그인 계정 — 관리자가 초기 비밀번호를 정해 발급한다.
 * 기존 `회원추가` 탭은 비밀번호를 만들지 않고 옛 MSSQL 에 써서 그 아이디로는 로그인이 되지 않는다(2026-09-17 실측).
 * 여기서 발급하면 로그인이 읽는 표에 가입과 같은 형식으로 들어간다. 비밀번호는 저장·응답 어디에도 평문으로 남지 않는다.
 */
function AccountsCard({ row, base }) {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ user_id: "", user_name: "", password: "", email: "", manager: false });
  const [msg, setMsg] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const load = useCallback(() => {
    call(base, `/api/onto/company/admin/accounts?business_no=${row.businessNo}`)
      .then((d) => setRows(d.accounts || [])).catch((e) => { setRows([]); setMsg(e.message); });
  }, [base, row.businessNo]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: k === "manager" ? e.target.checked : e.target.value }));
  const issue = () => call(base, "/api/onto/company/admin/issue-account", { business_no: row.businessNo, ...f })
    .then((d) => { setMsg(`${d.user_id} 발급 — ${d.message}`); setF({ user_id: "", user_name: "", password: "", email: "", manager: false }); load(); })
    .catch((e) => setMsg(e.message));
  const reset = () => call(base, "/api/onto/company/admin/reset-password", { business_no: row.businessNo, user_id: resetFor, password: resetPw })
    .then((d) => { setMsg(`${resetFor} · ${d.message}`); setResetFor(null); setResetPw(""); load(); })
    .catch((e) => setMsg(e.message));
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #eee" }} data-testid="admin-company-accounts">
      <b>로그인 계정</b> <span style={S.muted}>초기 비밀번호는 저장하지 않습니다 — 발급 뒤 사용자에게 직접 전달하세요.</span>
      {rows === null ? <div style={S.muted}>불러오는 중…</div> : (
        <table style={S.table}>
          <thead><tr><th style={S.th}>아이디</th><th style={S.th}>이름</th><th style={S.th}>이메일</th><th style={S.th}>매니저</th><th style={S.th}>발급일</th><th style={S.th}></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td style={{ ...S.td, ...S.muted }} colSpan={6}>발급된 계정이 없습니다.</td></tr>}
            {rows.map((a) => (
              <tr key={a.userId} data-testid="company-account">
                <td style={{ ...S.td, fontWeight: 600 }}>{a.userId}<div style={S.muted}>업체코드 {a.groupId}</div></td>
                <td style={S.td}>{a.userName || "—"}</td>
                <td style={S.td}>{a.userEmail || "—"}</td>
                <td style={S.td}>{a.managerYn === "Y" ? "매니저" : "—"}</td>
                <td style={S.td}>{a.regDt || "—"}{a.hasPassword ? "" : <div style={{ color: "#b91c1c" }}>비밀번호 없음 · 로그인 불가</div>}</td>
                <td style={S.td}><button style={S.btn} onClick={() => { setResetFor(a.userId); setResetPw(""); }}>비밀번호 변경</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {resetFor && (
        <div style={{ ...S.row }} data-testid="account-reset">
          <b>{resetFor}</b>
          <input style={{ ...S.input, minWidth: 200 }} type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="새 비밀번호(8자 이상)" />
          <button style={S.primary} onClick={reset} disabled={resetPw.length < 8}>변경</button>
          <button style={S.btn} onClick={() => setResetFor(null)}>취소</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px 12px", marginTop: 10 }}>
        <div><label style={S.label}>아이디 *</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.user_id} onChange={set("user_id")} placeholder="hong" data-testid="issue-user-id" /></div>
        <div><label style={S.label}>이름 *</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.user_name} onChange={set("user_name")} data-testid="issue-user-name" /></div>
        <div><label style={S.label}>초기 비밀번호 * <span style={{ fontWeight: 400 }}>8자 이상</span></label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} type="text" value={f.password} onChange={set("password")} data-testid="issue-password" /></div>
        <div><label style={S.label}>이메일</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.email} onChange={set("email")} /></div>
      </div>
      <div style={S.row}>
        <label style={{ display: "inline-flex", gap: 5, alignItems: "center" }}><input type="checkbox" checked={f.manager} onChange={set("manager")} data-testid="issue-manager" /> 매니저(회사 전체 열람)</label>
        <button style={S.primary} onClick={issue} disabled={!f.user_id.trim() || !f.user_name.trim() || f.password.length < 8} data-testid="issue-submit">계정 발급</button>
        {msg && <span>{msg}</span>}
      </div>
    </div>
  );
}

function ReviewCard({ row, base, onClose, onDone }) {
  const [f, setF] = useState({ status: row.status || "pending", office_ids: row.officeIds || "", domestic_codes: row.domesticCodes || "", review_note: row.reviewNote || "" });
  const [c, setC] = useState({ agreed: true, evidence: "" });
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const review = () => call(base, "/api/onto/company/admin/review", { business_no: row.businessNo, status: f.status, office_ids: f.office_ids, domestic_codes: f.domestic_codes, review_note: f.review_note })
    .then(() => { setMsg("검토 결과를 저장했습니다."); onDone(); }).catch((e) => setMsg(e.message));
  const consent = () => call(base, "/api/onto/company/admin/consent", { business_no: row.businessNo, agreed: c.agreed, evidence: c.evidence })
    .then(() => { setMsg("대리 기록을 저장했습니다."); onDone(); }).catch((e) => setMsg(e.message));
  return (
    <div style={S.card} data-testid="admin-company-review">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <b>검토 · {formatBusinessNo(row.businessNo)} {row.corpName || ""}</b>
        <button style={S.btn} onClick={onClose}>닫기</button>
      </div>
      {!row.corpName && <div style={{ ...S.muted, marginTop: 4 }}>회사 행이 없는 사업자(가입 화면 동의만 있음)입니다. 아래 "회사 직접 등록"으로 행을 먼저 만드세요.</div>}
      {row.corpName && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", marginTop: 8 }}>
          <div><label style={S.label}>상태</label>
            <select style={{ ...S.input, width: "100%" }} value={f.status} onChange={set("status")}>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label style={S.label}>검토 메모(사업자등록증 확인 등)</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.review_note} onChange={set("review_note")} /></div>
          <div><label style={S.label}>관세사 사무소 id(쉼표)</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.office_ids} onChange={set("office_ids")} placeholder="1005, 1144" /></div>
          <div><label style={S.label}>화주 사업자번호(쉼표, 10자리)</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.domestic_codes} onChange={set("domestic_codes")} placeholder="1234567890" /></div>
          <div style={{ gridColumn: "1 / -1" }}><button style={S.primary} onClick={review}>검토 저장</button></div>
        </div>
      )}
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #eee" }}>
        <b>정보공개 동의 대리 기록</b> <span style={S.muted}>서면 동의서 등 근거를 반드시 적습니다(기록에 남음).</span>
        <div style={{ ...S.row, marginTop: 6 }}>
          <select style={S.input} value={c.agreed ? "1" : "0"} onChange={(e) => setC((s) => ({ ...s, agreed: e.target.value === "1" }))}>
            <option value="1">동의</option><option value="0">철회</option>
          </select>
          <input style={{ ...S.input, flex: 1, minWidth: 240 }} value={c.evidence} onChange={(e) => setC((s) => ({ ...s, evidence: e.target.value }))} placeholder="예: 서면 동의서 2026-09-16 수령 · 담당 김OO" />
          <button style={S.btn} onClick={consent} disabled={!c.evidence.trim()}>기록</button>
        </div>
      </div>
      {msg && <div style={{ marginTop: 8 }}>{msg}</div>}
      {row.corpName && <AccountsCard row={row} base={base} />}
    </div>
  );
}

function CreateCard({ base, onDone }) {
  const [f, setF] = useState({ business_no: "", corp_name: "", corp_name_en: "", ceo_name: "", corp_addr: "", contact_name: "", contact_phone: "", contact_email: "", role: "", status: "pending", customs_office: "", nts_status: "", biz_type: "", biz_item: "" });
  const [msg, setMsg] = useState(null);
  const [looking, setLooking] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const create = () => call(base, "/api/onto/company/admin/create", { ...f, role: f.role || null })
    .then((d) => { setMsg(d.next || "등록했습니다."); onDone(); }).catch((e) => setMsg(e.message));
  // 66차: 사업자번호 조회(관세청 통관고유부호 + 국세청) → 상호·대표자·주소·전화·관할세관·상태 자동 채움(malso 거래처 등록과 같은 원천)
  const lookup = () => {
    const d = f.business_no.replace(/\D/g, "");
    if (d.length !== 10) { setMsg("사업자번호 10자리를 입력하세요."); return; }
    setLooking(true); setMsg(null);
    call(base, `/api/onto/company/biz-lookup?biz_no=${d}`)
      .then((r) => {
        if (!r.found) { setMsg(r.message || "조회 결과가 없습니다."); return; }
        setF((s) => fillFromLookup(s, r.info));
        setMsg(`조회 완료 — ${r.info.corp_name}${r.info.nts_status ? ` · 국세청 ${r.info.nts_status}` : ""}${r.info.customs_office ? ` · ${r.info.customs_office}` : ""}. 확인 후 등록하세요.`);
      })
      .catch((e) => setMsg(e.message)).finally(() => setLooking(false));
  };
  const onBizKey = (e) => { if (e.key === "Enter") { e.preventDefault(); lookup(); } };
  return (
    <div style={S.card} data-testid="admin-company-create">
      <b>회사 직접 등록(관리자 기반)</b>
      <div style={S.muted}>가입 없이 회사 행을 만듭니다. 사업자번호를 넣고 <b>조회</b>하면 관세청·국세청 정보로 채워집니다. 만든 뒤 <b>회원추가</b> 탭에서 업체코드(사업자번호 앞 6자리)로 로그인 계정을 발급하세요.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px 12px", marginTop: 8 }}>
        <div>
          <label style={S.label}>사업자번호 *</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input style={{ ...S.input, flex: 1, minWidth: 0 }} value={f.business_no} onChange={set("business_no")} onKeyDown={onBizKey} placeholder="123-45-67890" data-testid="create-biz-no" />
            <button type="button" style={S.btn} onClick={lookup} disabled={looking} data-testid="create-biz-lookup">{looking ? "조회 중…" : "조회"}</button>
          </div>
          {(f.nts_status || f.customs_office) && <div style={{ ...S.muted, marginTop: 3 }} data-testid="create-biz-meta">{[f.nts_status && `국세청 ${f.nts_status}`, f.customs_office, f.biz_type && `${f.biz_type} · ${f.biz_item}`].filter(Boolean).join(" · ")}</div>}
        </div>
        <div><label style={S.label}>회사명 *</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.corp_name} onChange={set("corp_name")} /></div>
        <div><label style={S.label}>대표자</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.ceo_name} onChange={set("ceo_name")} /></div>
        <div><label style={S.label}>역할</label>
          <select style={{ ...S.input, width: "100%" }} value={f.role} onChange={set("role")}><option value="">선택</option>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div style={{ gridColumn: "1 / 3" }}><label style={S.label}>주소</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.corp_addr} onChange={set("corp_addr")} data-testid="create-corp-addr" /></div>
        <div><label style={S.label}>영문 상호</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.corp_name_en} onChange={set("corp_name_en")} /></div>
        <div><label style={S.label}>담당자</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.contact_name} onChange={set("contact_name")} /></div>
        <div><label style={S.label}>전화</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.contact_phone} onChange={set("contact_phone")} data-testid="create-phone" /></div>
        <div><label style={S.label}>이메일</label><input style={{ ...S.input, width: "100%", boxSizing: "border-box" }} value={f.contact_email} onChange={set("contact_email")} /></div>
        <div><label style={S.label}>초기 상태</label>
          <select style={{ ...S.input, width: "100%" }} value={f.status} onChange={set("status")}>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
      </div>
      <div style={{ ...S.row }}>
        <button style={S.primary} onClick={create} disabled={!f.business_no.trim() || !f.corp_name.trim()}>회사 등록</button>
        {msg && <span>{msg}</span>}
      </div>
    </div>
  );
}
